import globalConstants from "src/theme/globalConstants";
import { SUPPLEMENTARY_FILTER_COLOURS } from "src/views/schedule/constants";

import { TSupplementaryRoles } from "../shared/constants/roles";

import { DISABLE_REASONS, TIsDisabled, TIsRegistered } from "./types";

export type TShiftScheduleEvent = {
  id: number;
  title: string;
  category: string;
  start: moment.Moment;
  end: moment.Moment;
  displayedStart: string;
  displayedEnd: string;
  numberOfBlocks: number;
  blocksFromTop: number;
  overlapColumn: number;
  registeredVolunteers: number;
  maxVolunteers: number;
  registrationOpen?: boolean;
  isRegistered?: TIsRegistered;
  isDisableRegistered?: TIsDisabled;
};

// The three days the calendar renders. blocksFromTop is measured from
// EVENT_DAYS[0], so this is the only place the window is defined.
export const EVENT_DAYS = ["2026-09-18", "2026-09-19", "2026-09-20"];

/**
 * Explicit layering. Shift tiles live inside an isolated section column, so
 * their z-index only ever competes with sibling tiles and can never reach the
 * pinned gutter or header.
 */
export const LAYER = {
  sectionColumn: 0,
  nowLine: 1,
  gutter: 2,
  header: 3,
  corner: 4,
} as const;

/** raised on hover/focus, scoped to the section column's stacking context */
export const TILE_RAISED_LAYER = 30;

/** one hour of vertical space; was the 65% setting on the old zoom control */
export const PX_PER_HOUR = 52;
/** width of a single lane inside a category section */
export const LANE_WIDTH = 150;

export const GUTTER_WIDTH = 70;
export const MIN_TILE_HEIGHT = 22;
export const HOURS_PER_DAY = 24;
export const TZ = "America/New_York";
export const DEFAULT_MIN_SHIFTS = 2;

export const eventWindowHours = (dayCount = EVENT_DAYS.length) =>
  dayCount * HOURS_PER_DAY;

// same overlap rule the calendar uses to keep tiles on the EVENT_DAYS canvas
export const overlapsEventWindow = (
  blocksFromTop: number,
  durationHours: number,
  dayCount = EVENT_DAYS.length
) =>
  blocksFromTop + durationHours > 0 &&
  blocksFromTop < eventWindowHours(dayCount);

/**
 * Rows shaded as overnight: midnight through 6am. Signup uses
 * isOvernightShift (either end in 12:00am–7:00am exactly).
 */
export const isOvernightHour = (hourOfDay: number) => hourOfDay < 7;

export const shiftColor = (category: string) =>
  SUPPLEMENTARY_FILTER_COLOURS[category as TSupplementaryRoles] ??
  (globalConstants?.color?.backgroundPrimary as string);

export const generateDisableReasonToolTip = (
  disable: DISABLE_REASONS,
  details?: string
): string => {
  switch (disable) {
    case DISABLE_REASONS.REGISTRATION_CLOSED:
      return "Registration isn't open";
    case DISABLE_REASONS.NO_SPOTS_LEFT:
      return "No more spots left!";
    case DISABLE_REASONS.TIME_CONFLICT:
      return "Time conflict with " + details;
    case DISABLE_REASONS.MIN_SIGN_UPS: {
      const parsed = Number(details);
      const count =
        Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_MIN_SHIFTS;
      return `Please sign up for your ${count} primary ${
        count === 1 ? "shift" : "shifts"
      } first`;
    }
    case DISABLE_REASONS.NOT_MATCHING_PRIMARY_ROLE:
      return "You can only sign up for " + details + " and Supplementary roles";
    default:
      return "";
  }
};

/** Short label for calendar chrome / a11y; tooltip keeps the longer reason. */
export const lockKindLabel = (kind?: DISABLE_REASONS): string => {
  switch (kind) {
    case DISABLE_REASONS.TIME_CONFLICT:
      return "Time conflict";
    case DISABLE_REASONS.NO_SPOTS_LEFT:
      return "Full";
    case DISABLE_REASONS.REGISTRATION_CLOSED:
      return "Not open";
    case DISABLE_REASONS.MIN_SIGN_UPS:
      return "Primary shifts required";
    case DISABLE_REASONS.NOT_MATCHING_PRIMARY_ROLE:
      return "Wrong role";
    default:
      return "Unavailable";
  }
};

export const FILTERNAMES = [
  "Activities",
  "Workshops",
  "Food",
  "Ceremonies",
  "Security",
  "Floor Patrol",
  "Supplementary",
];

export const PRIMARY_ROLES_ONLY = [
  "Activities",
  "Workshops",
  "Food",
  "Ceremonies",
];
export const PRIMARY_ROLES = [
  "Activities",
  "Workshops",
  "Food",
  "Ceremonies",
  "Security",
  "Floor Patrol",
];
export const ADDITIONAL_ROLES = ["Security", "Floor Patrol", "Supplementary"];
